// DashboardPage — Prop-firm dashboard (matches funderprofutures.com marketing)
// Layout (matches the marketing message + base44 design language):
//   1. Top header (avatar chevron + EN + bell + avatar dropdown) — PortalLayout
//   2. Hero card: Account balance + eye toggle + Buy/Sell (Activate New Challenge / Withdraw)
//   3. Active challenge card (tier, platform, size, progress bar, drawdown meter)
//   4. AI Bot Trader card (status, today's P&L, win rate, start/stop)
//   5. Free Challenge Bonus card (promo)
//   6. Equity curve (the funded account balance over time)
//   7. Recent trades
//   8. Services grid

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Empty, ErrorBox, Loading, PortfolioCard, RangeKey, SectionHeader, Sparkline, TimeRange } from './UIShell';
import { TfCard, TfCardHero, TfCardFloating, TfButton, TfBadge, TfSparkline, TfStatusDot, TfProgress, TfSkeleton, TfRewardCard, TfSectionHeaderView, TfTimeRange, TfHoldingRow, TfAssetHeader, TfSparklineMarker, TfStatRow } from '../../components/TfPrimitives';
import { BrandLogo, TierChip, TierPill, PlatformLogo, UserAvatar, PartnerLogo, PartnerStrip } from '../../components/Brand';
import { APP_ID } from '@funderpro/shared';
import { getToken, getUser, API_BASE } from '../../lib/auth';
import { TIERS, DEMO_CHALLENGE, DEMO_BOT, BOT_STRATEGIES, TIER_ORDER, TIER_COMPARISON } from '../../data/platforms';
import { BrandConstellation, FreeStockRewardSheet } from './RewardComponents';
import { ChatBubble } from './ChatBubble';

interface ChartPoint { progress_date: string; account_balance: number; daily_pnl: number; total_pnl_pct: number; standing: number; }

export function DashboardPage() {
  const nav = useNavigate();
  const data = useData();
  const [range, setRange] = useState<RangeKey>('1M');
  const [rewardOpen, setRewardOpen] = useState(false);

  // Read the user's current tier (defaults to the challenge tier they bought, or Beginner)
  const userTier: keyof typeof TIERS = (data.value?.user?.tier || DEMO_CHALLENGE.tier) as keyof typeof TIERS;
  const challenge = data.value?.challenge || DEMO_CHALLENGE;
  const bot = data.value?.bot || DEMO_BOT;
  const tier = TIERS[userTier] || TIERS.Beginner;

  // Equity curve
  const all = data.value?.chart || [];
  const sliceMap: Record<RangeKey, number> = { '1D': 1, '7D': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, 'ALL': all.length };
  const points = all.slice(-Math.max(1, sliceMap[range])).map((p: ChartPoint) => p.account_balance);
  const firstP = all[0]?.account_balance ?? challenge.profit;
  const lastP = all[all.length - 1]?.account_balance ?? challenge.profit;
  const changeAbs = lastP - firstP;
  const change = firstP ? (changeAbs / firstP) * 100 : 0;

  if (data.error) return <div className="p-4"><ErrorBox>{data.error}</ErrorBox></div>;
  if (!data.value) return <Loading label="Loading dashboard…" />;

  return (
    <div className="relative">
      {/* iOS 26 Greeting */}
      <div className="px-4 pt-2 pb-1 flex items-center gap-3 tf-anim-fadeIn">
        <UserAvatar size={36} name='Brandon' />
        <div>
          <div className="text-[11px] text-tf-text-3">{new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 18 ? 'Good afternoon' : 'Good evening'}</div>
          <div className="text-[17px] font-semibold tracking-tight">Hi, Brandon 👋</div>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <TfStatusDot pulsing />
          <span className="text-[10px] font-semibold text-tf-profit uppercase tracking-wider">Live</span>
        </div>
      </div>
      {/* Top hero (subtle white-to-light) */}
      <div className="h-10 bg-gradient-to-b from-white to-[#F2F2F7]" />

      {/* Account balance + trade actions */}
      <PortfolioCard
        value={data.value.user.balance}
        change={change}
        changeAbs={changeAbs}
        when={all.length ? new Date(all[all.length - 1].progress_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : 'Today'}
        primaryAction={undefined}
        buyAction={{ label: 'Activate Challenge', onClick: () => nav('/portal/discover') }}
        sellAction={{ label: 'Withdraw', onClick: () => nav('/portal/wallet/withdraw') }}
      />

      {/* Active Challenge card — the centerpiece of the prop-firm product */}
      <ActiveChallengeCard
        challenge={challenge}
        onOpen={() => nav('/portal/challenge/' + challenge.id)}
        onBot={() => nav('/portal/bot')}
      />

      {/* AI Bot Trader card */}
      <BotStatusCard
        bot={bot}
        onOpen={() => nav('/portal/bot')}
        tier={tier}
      />

      {/* Free challenge bonus card — new prop-firm version (claim activation bonus / unlock funded days) */}
      <TfRewardCard
        testId="dashboard-reward"
        title="Claim $30 platform activation bonus"
        description="Activate any platform before expiry"
        countdown="1d 23h 59m 34s"
        ctaLabel="Claim"
        onCta={() => setRewardOpen(true)}
      />

      {/* Equity curve chart — prop-firm spec: "Equity curve" + smooth area fill + active marker + time pills */}
      <TfCard className="mx-4 mt-3" testId="equity-curve-card">
        <div className="px-1">
          <TfAssetHeader
            title="Equity curve"
            price={data.value.user.balance}
            changePct={change}
            changeAbs={changeAbs}
            period="since challenge start"
            testId="equity-header"
          />
        </div>
        <div className="-mx-2 -mt-1">
          <TfSparklineMarker
            points={points.length > 1 ? points : [100, 100, 100, 100]}
            color={change >= 0 ? '#007AFF' : '#FF3B30'}
            fillColor={change >= 0 ? 'rgba(0,122,255,0.10)' : 'rgba(255,59,48,0.10)'}
            height={140}
            markerLabel={`$${Math.round(lastP).toLocaleString()}`}
            markerPct={change}
            markerIndex={Math.floor(points.length * 0.6)}
            testId="equity-spark-marker"
          />
        </div>
        <div className="-mx-1">
          <TfTimeRange value={range} onChange={(v) => setRange(v as RangeKey)} options={['1D', '7D', '1M', '3M', '6M', '1Y', 'ALL']} testId="equity-time-range" />
        </div>
      </TfCard>

      {/* Your challenges — prop-firm list using holding-row pattern (icon + name + avg + sparkline + % change) */}
      <TfSectionHeaderView title="Your challenges" viewAllLabel="View all" onViewAll={() => nav('/portal/challenges')} testId="challenges-header" />
      <div className="mx-4 overflow-hidden rounded-[16px] bg-white" data-testid="challenges-list">
        <TfHoldingRow
          icon={<span className="text-[18px]">⚡</span>}
          iconBg="#FF9500"
          name={`${tier.key} · ${challenge.platformDisplay}`}
          ticker={challenge.platform?.toUpperCase()}
          avgPrice={`$${challenge.size}K`}
          shares={`${challenge.daysActive}/${tier.fundedDaysMin ?? 5} days`}
          currentPrice={`$${challenge.profit?.toLocaleString() ?? 0}`}
          changePct={change}
          sparkline={points.length > 1 ? points : [100, 100, 100]}
          onClick={() => nav('/portal/challenge/' + challenge.id)}
          testId="challenge-row-1"
        />
        <div className="mx-4 border-t border-[#F2F2F7]" />
        <TfHoldingRow
          icon={<span className="text-[18px]">🎯</span>}
          iconBg="#5856D6"
          name="Starter Challenge"
          ticker="NINJATRADER"
          avgPrice="$25K"
          shares="0/5 days"
          currentPrice="$0.00"
          changePct={0}
          sparkline={[100, 100, 100, 100]}
          onClick={() => nav('/portal/challenge-pricing')}
          testId="challenge-row-2"
        />
      </div>

      {/* Tier quick-look + Upgrade (using new section header pattern) */}
      <TfSectionHeaderView title="Your tier" viewAllLabel="Compare" onViewAll={() => nav('/portal/tiers')} testId="tier-header" />
      <div className="mx-4 overflow-hidden rounded-[16px] bg-white" data-testid="tier-card">
        <TierSummary tier={userTier} />
        <div className="border-t border-tf-border px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[13px] text-tf-text-3">Profit split</div>
              <div className="text-[20px] font-bold tabular-nums text-tf-text-1">{tier.profitSplit}%</div>
            </div>
            <div>
              <div className="text-[13px] text-tf-text-3">Max challenges</div>
              <div className="text-[20px] font-bold tabular-nums text-tf-text-1">{tier.maxActiveChallenges}</div>
            </div>
            <div>
              <div className="text-[13px] text-tf-text-3">Bot strategies</div>
              <div className="text-[20px] font-bold tabular-nums text-tf-text-1">{tier.botStrategiesAvailable}/4</div>
            </div>
            <button onClick={() => nav('/portal/tiers')} className="rounded-full bg-tf-text-1 px-3 py-1.5 text-[13px] font-semibold text-white">
              Upgrade
            </button>
          </div>
        </div>
      </div>

      {/* Services */}
      <TfSectionHeaderView title="Services" testId="services-header" />
      <div className="mx-4 grid grid-cols-2 gap-3 pb-3">
        <ServiceTile to="/portal/wallet/deposit" icon="wallet" label="Wallet" />
        <ServiceTile to="/portal/platforms" icon="platforms" label="Platforms" />
        <ServiceTile to="/portal/economic-calendar" icon="calendar" label="Calendar" />
        <ServiceTile to="/portal/affiliate" icon="gift" label="Affiliate" />
      </div>

      {/* Trust badges — real partner logos */}
      <div className="mx-4 mt-4 mb-2 px-2">
        <div className="text-[10px] uppercase tracking-wider text-tf-text-3 text-center mb-2">Powered by</div>
        <PartnerStrip partners={['persona', 'stripe', 'rithmic', 'twelvedata', 'tradingview']} height={22} />
      </div>

      {/* Reward sheet (modal) */}
      <FreeStockRewardSheet open={rewardOpen} onClose={() => setRewardOpen(false)} onClaim={() => { setRewardOpen(false); nav('/portal/rewards/claim'); }} />

      {/* Floating chat bubble (matches base44 dark Orders page) */}
      <ChatBubble className="fixed bottom-24 right-4 z-30" />
    </div>
  );
}

// ─── Active Challenge card ─────────────────────────────────────────────────

function ActiveChallengeCard({ challenge, onOpen, onBot }: { challenge: any; onOpen: () => void; onBot: () => void }) {
  const tier = TIERS[challenge.tier as keyof typeof TIERS] || TIERS.Beginner;
  const progressPct = Math.min(100, (challenge.profit / challenge.targetProfit) * 100);
  const drawdownPct = Math.min(100, (challenge.profit < 0 ? -challenge.profit : 0) / challenge.maxDrawdown * 100);
  const dailyUsedPct = Math.min(100, Math.max(0, 30)); // demo: 30% of daily loss used
  return (
    <div className="mx-4 mt-3 overflow-hidden rounded-[20px] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]" data-testid="active-challenge">
      {/* Header */}
      <button onClick={onOpen} className="flex w-full items-center justify-between p-4 text-left active:bg-tf-surface-1">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-[12px] bg-white border border-tf-border">
            <PlatformLogo name={challenge.platform} size={28} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <TierPill tier={challenge.tier} />
              <span className="text-[12px] text-tf-text-3">· ${challenge.size}K funded</span>
            </div>
            <div className="mt-0.5 text-[15px] font-semibold text-tf-text-1">{challenge.platformDisplay} · {tier.profitSplit}% split</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-tf-text-3">Status</div>
          <div className="text-[13px] font-semibold text-tf-profit capitalize">{challenge.status}</div>
        </div>
      </button>

      {/* Progress: profit target */}
      <div className="px-4 pb-2">
        <div className="flex items-end justify-between mb-1">
          <div>
            <div className="text-[11px] text-tf-text-3">Profit target</div>
            <div className="text-[15px] font-bold tabular-nums text-tf-text-1">${challenge.profit.toLocaleString()} <span className="text-[12px] text-tf-text-3 font-normal">/ ${challenge.targetProfit.toLocaleString()}</span></div>
          </div>
          <div className="text-[13px] font-semibold tabular-nums" style={{ color: tier.badgeColor }}>{progressPct.toFixed(0)}%</div>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-tf-surface-1">
          <div className="h-full rounded-full transition-all" style={{ width: progressPct + '%', backgroundColor: tier.badgeColor }} />
        </div>
      </div>

      {/* Drawdown meter */}
      <div className="px-4 pb-2">
        <div className="flex items-end justify-between mb-1">
          <div>
            <div className="text-[11px] text-tf-text-3">Daily loss limit</div>
            <div className="text-[15px] font-bold tabular-nums text-tf-text-1">${(challenge.dailyDrawdown * dailyUsedPct / 100).toFixed(0)} <span className="text-[12px] text-tf-text-3 font-normal">/ ${challenge.dailyDrawdown.toLocaleString()}</span></div>
          </div>
          <div className="text-[13px] font-semibold tabular-nums text-tf-text-1">{dailyUsedPct.toFixed(0)}% used</div>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-tf-surface-1">
          <div className="h-full rounded-full bg-[#FF9500] transition-all" style={{ width: dailyUsedPct + '%' }} />
        </div>
      </div>

      {/* Footer: days active + open bot button */}
      <div className="flex items-center justify-between border-t border-tf-border px-4 py-3">
        <div className="text-[12px] text-tf-text-3">Day {challenge.daysActive} of {challenge.daysRemaining + challenge.daysActive}</div>
        <button onClick={onBot} data-testid="open-bot" className="flex items-center gap-1.5 text-[13px] font-semibold text-tf-blue">
          <span className="h-2 w-2 rounded-full bg-[#34C759] animate-pulse" /> Open AI Bot →
        </button>
      </div>
    </div>
  );
}

// ─── AI Bot status card ───────────────────────────────────────────────────

function BotStatusCard({ bot, onOpen, tier }: { bot: any; onOpen: () => void; tier: any }) {
  const strategy = BOT_STRATEGIES.find(s => s.key === bot.strategy);
  const tierOK = tier.botStrategiesAvailable >= BOT_STRATEGIES.findIndex(s => s.key === bot.strategy) + 1;
  return (
    <button onClick={onOpen} data-testid="bot-card" className="mx-4 mt-3 flex w-[calc(100%-2rem)] items-center justify-between overflow-hidden rounded-[20px] bg-gradient-to-br from-[#1C1C1E] to-[#2A2A2A] p-4 text-left text-white shadow-[0_8px_30px_rgba(0,0,0,0.10)] active:scale-[0.99]">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-[12px] bg-white/10 text-[24px]">
          {strategy?.icon || '🤖'}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-white">AI Bot Trader</span>
            <span className={['flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
              bot.status === 'running' ? 'bg-[#34C759]/20 text-tf-profit' : 'bg-white/15 text-white/70'].join(' ')}>
              <span className={['h-1.5 w-1.5 rounded-full', bot.status === 'running' ? 'bg-[#34C759] animate-pulse' : 'bg-white/60'].join(' ')} />
              {bot.status}
            </span>
          </div>
          <div className="mt-0.5 text-[12px] text-white/70">{strategy?.name} · {bot.platform} · {tierOK ? `${tier.maxBotsAtOnce} bot max` : 'tier locked'}</div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-[11px] text-white/60">Today</div>
        <div className={['text-[16px] font-bold tabular-nums', bot.todayPnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
          {bot.todayPnl >= 0 ? '+' : ''}${bot.todayPnl.toFixed(2)}
        </div>
        <div className={['text-[11px] tabular-nums', bot.todayPct >= 0 ? 'text-tf-profit/80' : 'text-tf-loss/80'].join(' ')}>
          {bot.todayPct >= 0 ? '+' : ''}{bot.todayPct.toFixed(2)}%
        </div>
      </div>
    </button>
  );
}

// ─── Tier summary row ─────────────────────────────────────────────────────

function TierSummary({ tier: tierKey }: { tier: string }) {
  const tier = TIERS[tierKey as keyof typeof TIERS] || TIERS.Beginner;
  return (
    <div className="p-4">
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-[14px]"
          style={{ background: 'linear-gradient(135deg, #34C759 0%, #248A3D 100%)' }}
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L4 7v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V7l-8-5z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold" style={{ color: '#1C1C1E' }}>{tierKey} tier</div>
          <div className="text-[12px]" style={{ color: '#8E8E93' }}>{tier.tagline}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Service tile ─────────────────────────────────────────────────────────

function ServiceTile({ to, icon, label }: { to: string; icon: string; label: string }) {
  const nav = useNavigate();
  return (
    <button
      onClick={() => nav(to)}
      data-testid={`link-${label.toLowerCase().replace(/\s/g, '-')}`}
      className="flex items-center gap-3 rounded-[16px] bg-white p-4 text-left active:scale-[0.98] transition"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-tf-blue/10 text-tf-blue">
        <ServiceIcon kind={icon} />
      </div>
      <div className="text-[15px] font-semibold text-tf-text-1">{label}</div>
    </button>
  );
}

function ServiceIcon({ kind }: { kind: string }) {
  if (kind === 'wallet') return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="14" rx="2" /><path d="M16 13h2" strokeLinecap="round" /></svg>;
  if (kind === 'platforms') return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7l9-4 9 4-9 4-9-4z" /><path d="M3 12l9 4 9-4M3 17l9 4 9-4" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  if (kind === 'calendar') return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" strokeLinecap="round" /></svg>;
  if (kind === 'gift') return <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="9" width="18" height="13" rx="2" /><path d="M3 9h18M12 9v13M12 9c-2-3-7-3-7 0 0 2 3 3 7 3M12 9c2-3 7-3 7 0 0 2-3 3-7 3" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  return null;
}

// ─── data hook (with demo seed) ───────────────────────────────────────────

function useData() {
  const [value, setValue] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const tok = getToken();
        // Always seed with demo data so the design is fully visible
        const seed = {
          user: { id: 'demo', email: 'demo@funderpro.live', full_name: 'Brandon', balance: 52340.50, kyc_status: 'approved', role: 'user', tier: 'Mid', created_at: new Date().toISOString() },
          challenge: DEMO_CHALLENGE,
          bot: DEMO_BOT,
          chart: genDemoChart(),
          active_subscriptions: [{ kind: 'challenge' }],
        };
        if (!tok || tok === 'mock' || tok === 'test' || tok.startsWith('mock')) {
          setValue(seed);
          return;
        }
        try {
          const res = await fetch(`${API_BASE}/api/apps/${APP_ID}/dashboard/summary`, { headers: { Authorization: `Bearer ${tok}` } });
          if (!res.ok) throw new Error('Failed to load');
          const summary = await res.json();
          setValue({ ...seed, ...summary, challenge: summary.challenge || DEMO_CHALLENGE, bot: summary.bot || DEMO_BOT });
        } catch {
          setValue(seed);
        }
      } catch (e: any) { setError(e?.message || 'Failed to load dashboard'); }
    })();
  }, []);
  return { value, error };
}

function genDemoChart(): ChartPoint[] {
  const out: ChartPoint[] = [];
  // Start near a realistic challenge starting balance so the % change makes sense
  const start = 50000;
  let v = start;
  for (let i = 0; i < 90; i++) {
    v += Math.sin(i * 0.3) * 80 + (Math.random() - 0.45) * 200;
    out.push({
      progress_date: new Date(Date.now() - (90 - i) * 86400000).toISOString(),
      account_balance: v,
      daily_pnl: (Math.random() - 0.4) * 200,
      total_pnl_pct: ((v - start) / start) * 100,
      standing: 1,
    });
  }
  return out;
}
